import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import {
  FaArrowLeft,
  FaMapMarkerAlt,
  FaWhatsapp,
  FaCalendarAlt,
  FaUsers,
  FaMapSigns,
  FaTools,
  FaCheck,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationCircle,
} from "react-icons/fa";
import { API_TRIP_URL, API_ROUTES_TRIP } from "../../config/apiConfig";
import "./TripView.css";

interface ScheduleItem {
  date: string;
  times: string[];
  descriptions: string[];
}

interface TripInfo {
  cognito_user_id: string;
  created_at: string;
  updated_at: string;
  tripName: string;
  tripImage: string;
  startDate: string;
  endDate: string;
  selectedRegion: string;
  selectedSubRegion: string;
  selectedTypes: string;
  selectedLevel: string;
  numberOfTravellers: string;
  shortDescription: string;
  longDescription: string;
  necessaryGears: string;
  necessaryGearsRemark: string;
  recommendedGears: string;
  recommendedGearsRemark: string;
  schedules: string;
  startingPointName: string;
  startingPointWazeLink: string;
  googleMapLink: string;
  whatsAppLink: string;
  isCancelled: boolean;
  tripNote: string;
}

const TripView: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();

  // Retrieve username from Redux state (user reducer)
  const username = useSelector((state: any) => state.user.username);

  const [tripInfo, setTripInfo] = useState<TripInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchTripInfo();
  }, [tripId]);

  const fetchTripInfo = async () => {
    try {
      const response = await axios.get<TripInfo>(
        `${API_TRIP_URL}${API_ROUTES_TRIP.trips}${tripId}`
      );
      setTripInfo(response.data);
      console.log(response.data);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const file = event.target.files[0];
    setPreviewImage(URL.createObjectURL(file));
    setSelectedFile(file);
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append("tripImage", selectedFile);
    setIsUploading(true);
    try {
      const response = await axios.put<{ tripImage: string }>(
        `${API_TRIP_URL}${API_ROUTES_TRIP.updateTripImage}${tripId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setTripInfo((prev) =>
        prev ? { ...prev, tripImage: response.data.tripImage } : prev
      );
      setPreviewImage(null);
      setSelectedFile(null);
    } catch (error) {
      console.error("Image update failed:", error);
      alert("Failed to upload trip image.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancelUpload = () => {
    setPreviewImage(null);
    setSelectedFile(null);
  };

  const deleteTrip = async () => {
    if (window.confirm("Are you sure you want to delete this trip?")) {
      try {
        await axios.delete(
          `${API_TRIP_URL}${API_ROUTES_TRIP.deleteTrip}${tripId}/`
        );
        alert("Trip deleted successfully!");
        navigate("/trips");
      } catch (error) {
        console.error("Error deleting trip:", error);
      }
    }
  };

  const schedules: ScheduleItem[] = tripInfo?.schedules
    ? JSON.parse(tripInfo.schedules)
    : [];

  const formatDate = (date: string): string =>
    dayjs(date).format("DD-MMM-YYYY");

  if (isLoading) return <div className="loading">Loading...</div>;

  return (
    <div className="trip-view">
      {tripInfo && (
        <>
          <div className="header">
            <div className="image-container">
              <img
                src={previewImage || tripInfo.tripImage}
                alt={tripInfo.tripName}
                className="header-image"
              />
              {isUploading ? (
                <div className="uploading-overlay">Uploading...</div>
              ) : (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    className="image-upload-input"
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />
                  {/* Render edit button if a user is authenticated */}
                  {username && (
                    <button className="edit-image-btn" onClick={handleEditClick}>
                      <FaEdit />
                    </button>
                  )}
                </>
              )}
              {previewImage && (
                <div className="preview-overlay">
                  <button
                    className="confirm-upload-btn"
                    onClick={handleConfirmUpload}
                  >
                    <FaCheckCircle /> Confirm Upload
                  </button>
                  <button
                    className="cancel-upload-btn"
                    onClick={handleCancelUpload}
                  >
                    <FaTimesCircle /> Cancel
                  </button>
                </div>
              )}
              <div className="overlay">
                <h1>{tripInfo.tripName}</h1>
                <div className="trip-meta">
                  <p>
                    <FaCalendarAlt /> {formatDate(tripInfo.startDate)} -{" "}
                    {formatDate(tripInfo.endDate)}
                  </p>
                  <p>
                    <FaMapSigns /> {tripInfo.selectedRegion} -{" "}
                    {tripInfo.selectedSubRegion}
                  </p>
                  <p>
                    <FaUsers /> {tripInfo.numberOfTravellers} Travellers
                  </p>
                </div>
              </div>
            </div>
            {/* Render back button if a user is authenticated */}
            {username && (
              <button onClick={() => navigate(-1)} className="back-button">
                <FaArrowLeft /> Back
              </button>
            )}
          </div>

          <div className="trip-info">
            <div className="info-section">
              <h3>Trip Details</h3>
              <p>
                <strong>Trip Level:</strong> {tripInfo.selectedLevel}
              </p>
              <p>
                <strong>Trip Type:</strong> {tripInfo.selectedTypes}
              </p>
              {tripInfo.isCancelled && (
                <p className="cancelled-trip">
                  <FaExclamationCircle /> This trip has been cancelled.
                </p>
              )}
            </div>

            <div className="info-section">
              <h3>Schedule</h3>
              {schedules.length > 0 ? (
                schedules.map((schedule, index) => (
                  <p key={index}>
                    <strong>{schedule.date}</strong> -{" "}
                    {schedule.times.join(", ")}
                  </p>
                ))
              ) : (
                <p>No schedule available.</p>
              )}
            </div>

            <div className="info-section">
              <h3>Long Description</h3>
              <p>{tripInfo.longDescription}</p>
            </div>

            <div className="info-section">
              <h3>Necessary Gears</h3>
              <ul>
                {tripInfo.necessaryGears.split(",").map((gear, index) => (
                  <li key={index}>
                    <FaCheck /> {gear.trim()}
                  </li>
                ))}
              </ul>
              {tripInfo.necessaryGearsRemark && (
                <p>
                  <strong>Note:</strong> {tripInfo.necessaryGearsRemark}
                </p>
              )}
            </div>

            <div className="info-section">
              <h3>Recommended Gears</h3>
              <ul>
                {tripInfo.recommendedGears.split(",").map((gear, index) => (
                  <li key={index}>
                    <FaCheck /> {gear.trim()}
                  </li>
                ))}
              </ul>
              {tripInfo.recommendedGearsRemark && (
                <p>
                  <strong>Note:</strong> {tripInfo.recommendedGearsRemark}
                </p>
              )}
            </div>

            <div className="info-section">
              <h3>Starting Point</h3>
              <p>
                <FaMapMarkerAlt /> {tripInfo.startingPointName}
              </p>
              <p>
                <a
                  href={tripInfo.startingPointWazeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in Waze
                </a>
              </p>
            </div>

            <div className="info-section">
              <h3>Trip Notes</h3>
              {tripInfo.tripNote ? (
                <p>{tripInfo.tripNote}</p>
              ) : (
                <p>No additional notes available.</p>
              )}
            </div>

            <div className="info-section">
              <h3>Links</h3>
              <p>
                <a
                  href={tripInfo.googleMapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Google Maps
                </a>
              </p>
              <p>
                <a
                  href={tripInfo.whatsAppLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Join WhatsApp Group
                </a>
              </p>
            </div>

            {/* Render delete button if a user is authenticated */}
            {username && (
              <button onClick={deleteTrip} className="delete-trip-btn">
                <FaTrash /> Delete Trip
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default TripView;




//--------------------------last before working code---------------------------

// import React, { useState, useEffect, useRef } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import axios from "axios";
// import dayjs from "dayjs";
// import {
//   FaArrowLeft,
//   FaMapMarkerAlt,
//   FaWhatsapp,
//   FaCalendarAlt,
//   FaUsers,
//   FaMapSigns,
//   FaTools,
//   FaCheck,
//   FaEdit,
//   FaTrash,
//   FaCheckCircle,
//   FaTimesCircle,
// } from "react-icons/fa";
// import { API_TRIP_URL, API_ROUTES_TRIP } from "../../config/apiConfig";
// import "./TripView.css";

// const TripView: React.FC = () => {
//   const { tripId } = useParams<{ tripId: string }>();
//   const navigate = useNavigate();
//   const [tripInfo, setTripInfo] = useState<any>(null);
//   const [isLoading, setIsLoading] = useState(true);
//   const [isUploading, setIsUploading] = useState(false);
//   const [previewImage, setPreviewImage] = useState<string | null>(null);
//   const [selectedFile, setSelectedFile] = useState<File | null>(null);
//   const fileInputRef = useRef<HTMLInputElement>(null);

//   useEffect(() => {
//     fetchTripInfo();
//   }, [tripId]);

//   const fetchTripInfo = async () => {
//     try {
//       const response = await axios.get(
//         `${API_TRIP_URL}${API_ROUTES_TRIP.trips}${tripId}`
//       );
//       setTripInfo(response.data);
//     } catch (error) {
//       console.error(error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleEditClick = () => {
//     fileInputRef.current?.click();
//   };

//   const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
//     if (!event.target.files || event.target.files.length === 0) return;

//     const file = event.target.files[0];

//     // **Show preview before upload**
//     setPreviewImage(URL.createObjectURL(file));
//     setSelectedFile(file);
//   };

//   const handleConfirmUpload = async () => {
//     if (!selectedFile) return;

//     const formData = new FormData();
//     formData.append("tripImage", selectedFile);

//     setIsUploading(true);
//     try {
//       const response = await axios.put(
//         `${API_TRIP_URL}${API_ROUTES_TRIP.updateTripImage}${tripId}`,
//         formData,
//         { headers: { "Content-Type": "multipart/form-data" } }
//       );

//       // **Update trip image and reset preview**
//       setTripInfo((prev: any) => ({
//         ...prev,
//         tripImage: response.data.tripImage,
//       }));
//       setPreviewImage(null);
//       setSelectedFile(null);
//     } catch (error) {
//       console.error("Image update failed:", error);
//       alert("Failed to upload trip image.");
//     } finally {
//       setIsUploading(false);
//     }
//   };

//   const handleCancelUpload = () => {
//     setPreviewImage(null);
//     setSelectedFile(null);
//   };

//   const deleteTrip = async () => {
//     if (window.confirm("Are you sure you want to delete this trip?")) {
//       try {
//         await axios.delete(
//           `${API_TRIP_URL}${API_ROUTES_TRIP.deleteTrip}${tripId}/`
//         );
//         alert("Trip deleted successfully!");
//         navigate("/trips");
//       } catch (error) {
//         console.error("Error deleting trip:", error);
//       }
//     }
//   };

//   const formatDate = (date: string): string =>
//     dayjs(date).format("DD-MMM-YYYY");

//   if (isLoading) return <div className="loading">Loading...</div>;

//   return (
//     <div className="trip-view">
//       {tripInfo && (
//         <>
//           <div className="header">
//             <div className="image-container">
//               <img
//                 src={previewImage || tripInfo.tripImage}
//                 alt={tripInfo.tripName}
//                 className="header-image"
//               />

//               {isUploading ? (
//                 <div className="uploading-overlay">Uploading...</div>
//               ) : (
//                 <>
//                   <input
//                     type="file"
//                     accept="image/*"
//                     ref={fileInputRef}
//                     className="image-upload-input"
//                     onChange={handleImageChange}
//                     style={{ display: "none" }}
//                   />
//                   <button className="edit-image-btn" onClick={handleEditClick}>
//                     <FaEdit />
//                   </button>
//                 </>
//               )}

//               {previewImage && (
//                 <div className="preview-overlay">
//                   <button
//                     className="confirm-upload-btn"
//                     onClick={handleConfirmUpload}
//                   >
//                     <FaCheckCircle /> Confirm Upload
//                   </button>
//                   <button
//                     className="cancel-upload-btn"
//                     onClick={handleCancelUpload}
//                   >
//                     <FaTimesCircle /> Cancel
//                   </button>
//                 </div>
//               )}

//               <div className="overlay">
//                 <h1>{tripInfo.tripName}</h1>
//                 <div className="trip-meta">
//                   <p>
//                     <FaCalendarAlt /> {formatDate(tripInfo.startDate)} -{" "}
//                     {formatDate(tripInfo.endDate)}
//                   </p>
//                   <p>
//                     <FaMapSigns /> {tripInfo.selectedRegion} -{" "}
//                     {tripInfo.selectedSubRegion}
//                   </p>
//                   <p>
//                     <FaUsers /> {tripInfo.numberOfTravellers} Travellers
//                   </p>
//                 </div>
//               </div>
//             </div>
//             <button onClick={() => navigate(-1)} className="back-button">
//               <FaArrowLeft /> Back
//             </button>
//           </div>

//           <div className="trip-info">
//             <div className="description-section">
//               <h3>Short Description</h3>
//               <p>{tripInfo.shortDescription}</p>
//               <h3>Long Description</h3>
//               <p>{tripInfo.longDescription}</p>
//             </div>

//             <div className="gear-section">
//               <h3>
//                 <FaTools /> Necessary Gears
//               </h3>
//               <ul>
//                 {tripInfo.necessaryGears
//                   .split(",")
//                   .map((gear: string, index: number) => (
//                     <li key={index}>
//                       <FaCheck /> {gear.trim()}
//                     </li>
//                   ))}
//               </ul>

//               <h3>
//                 <FaTools /> Recommended Gears
//               </h3>
//               <ul>
//                 {tripInfo.recommendedGears
//                   .split(",")
//                   .map((gear: string, index: number) => (
//                     <li key={index}>
//                       <FaCheck /> {gear.trim()}
//                     </li>
//                   ))}
//               </ul>
//             </div>

//             <p>
//               <strong>Starting Point:</strong> {tripInfo.startingPointName}
//             </p>

//             <div className="map-links">
//               <p>
//                 <FaMapMarkerAlt />{" "}
//                 <a
//                   href={tripInfo.googleMapLink}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                 >
//                   View on Google Maps
//                 </a>
//               </p>
//               <p>
//                 <FaWhatsapp />{" "}
//                 <a
//                   href={tripInfo.whatsAppLink}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                 >
//                   Open in WhatsApp
//                 </a>
//               </p>
//             </div>

//             <button onClick={deleteTrip} className="delete-trip-btn">
//               <FaTrash /> Delete Trip
//             </button>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default TripView;

//-----------------------------------------------------------

// import React, { useState, useEffect, useRef } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import axios from "axios";
// import dayjs from "dayjs";
// import {
//   FaArrowLeft,
//   FaMapMarkerAlt,
//   FaWhatsapp,
//   FaCalendarAlt,
//   FaUsers,
//   FaMapSigns,
//   FaTools,
//   FaCheck,
//   FaEdit,
//   FaTrash,
// } from "react-icons/fa";
// import { API_TRIP_URL, API_ROUTES_TRIP } from "../../config/apiConfig";
// import "./TripView.css";

// const TripView: React.FC = () => {
//   const { tripId } = useParams<{ tripId: string }>();
//   const navigate = useNavigate();
//   const [tripInfo, setTripInfo] = useState<any>(null);
//   const [isLoading, setIsLoading] = useState(true);
//   const [isUploading, setIsUploading] = useState(false);
//   const fileInputRef = useRef<HTMLInputElement>(null);

//   useEffect(() => {
//     fetchTripInfo();
//   }, [tripId]);

//   const fetchTripInfo = async () => {
//     try {
//       const response = await axios.get(
//         `${API_TRIP_URL}${API_ROUTES_TRIP.trips}${tripId}`
//       );
//       setTripInfo(response.data);
//     } catch (error) {
//       console.error(error);
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   const handleEditClick = () => {
//     if (fileInputRef.current) {
//       fileInputRef.current.click(); // Triggers file input click
//     }
//   };

//   const handleImageChange = async (
//     event: React.ChangeEvent<HTMLInputElement>
//   ) => {
//     if (!event.target.files || event.target.files.length === 0) return;

//     const file = event.target.files[0];
//     const formData = new FormData();
//     formData.append("tripImage", file);

//     setIsUploading(true);
//     try {
//       const response = await axios.put(
//         `${API_TRIP_URL}${API_ROUTES_TRIP.trips}${tripId}/update-image`,
//         formData,
//         { headers: { "Content-Type": "multipart/form-data" } }
//       );
//       setTripInfo((prev: any) => ({
//         ...prev,
//         tripImage: response.data.tripImage,
//       }));
//     } catch (error) {
//       console.error("Image update failed:", error);
//     } finally {
//       setIsUploading(false);
//     }
//   };

//   const deleteTrip = async () => {
//     if (window.confirm("Are you sure you want to delete this trip?")) {
//       try {
//         await axios.delete(
//           `${API_TRIP_URL}${API_ROUTES_TRIP.deleteTrip}${tripId}/`
//         );
//         alert("Trip deleted successfully!");
//         navigate("/trips");
//       } catch (error) {
//         console.error("Error deleting trip:", error);
//       }
//     }
//   };

//   const formatDate = (date: string): string =>
//     dayjs(date).format("DD-MMM-YYYY");

//   if (isLoading) return <div className="loading">Loading...</div>;

//   return (
//     <div className="trip-view">
//       {tripInfo && (
//         <>
//           <div className="header">
//             <div className="image-container">
//               <img
//                 src={tripInfo.tripImage}
//                 alt={tripInfo.tripName}
//                 className="header-image"
//               />

//               {isUploading ? (
//                 <div className="uploading-overlay">Uploading...</div>
//               ) : (
//                 <>
//                   <input
//                     type="file"
//                     accept="image/*"
//                     ref={fileInputRef}
//                     className="image-upload-input"
//                     onChange={handleImageChange}
//                     style={{ display: "none" }} // Hidden input
//                   />
//                   <button className="edit-image-btn" onClick={handleEditClick}>
//                     <FaEdit />
//                   </button>
//                 </>
//               )}

//               <div className="overlay">
//                 <h1>{tripInfo.tripName}</h1>
//                 <div className="trip-meta">
//                   <p>
//                     <FaCalendarAlt /> {formatDate(tripInfo.startDate)} -{" "}
//                     {formatDate(tripInfo.endDate)}
//                   </p>
//                   <p>
//                     <FaMapSigns /> {tripInfo.selectedRegion} -{" "}
//                     {tripInfo.selectedSubRegion}
//                   </p>
//                   <p>
//                     <FaUsers /> {tripInfo.numberOfTravellers} Travellers
//                   </p>
//                 </div>
//               </div>
//             </div>
//             <button onClick={() => navigate(-1)} className="back-button">
//               <FaArrowLeft /> Back
//             </button>
//           </div>

//           <div className="trip-info">
//             <div className="description-section">
//               <h3>Short Description</h3>
//               <p>{tripInfo.shortDescription}</p>
//               <h3>Long Description</h3>
//               <p>{tripInfo.longDescription}</p>
//             </div>

//             <div className="gear-section">
//               <h3>
//                 <FaTools /> Necessary Gears
//               </h3>
//               <ul>
//                 {tripInfo.necessaryGears
//                   .split(",")
//                   .map((gear: string, index: number) => (
//                     <li key={index}>
//                       <FaCheck /> {gear.trim()}
//                     </li>
//                   ))}
//               </ul>

//               <h3>
//                 <FaTools /> Recommended Gears
//               </h3>
//               <ul>
//                 {tripInfo.recommendedGears
//                   .split(",")
//                   .map((gear: string, index: number) => (
//                     <li key={index}>
//                       <FaCheck /> {gear.trim()}
//                     </li>
//                   ))}
//               </ul>
//             </div>

//             <p>
//               <strong>Starting Point:</strong> {tripInfo.startingPointName}
//             </p>

//             <div className="map-links">
//               <p>
//                 <FaMapMarkerAlt />{" "}
//                 <a
//                   href={tripInfo.googleMapLink}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                 >
//                   View on Google Maps
//                 </a>
//               </p>
//               <p>
//                 <FaMapMarkerAlt />{" "}
//                 <a
//                   href={tripInfo.startingPointWazeLink}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                 >
//                   View on Waze
//                 </a>
//               </p>
//               <p>
//                 <FaWhatsapp />{" "}
//                 <a
//                   href={tripInfo.whatsAppLink}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                 >
//                   Open in WhatsApp
//                 </a>
//               </p>
//             </div>

//             <button onClick={deleteTrip} className="delete-trip-btn">
//               <FaTrash /> Delete Trip
//             </button>
//           </div>
//         </>
//       )}
//     </div>
//   );
// };

// export default TripView;
